/* eslint-disable style/jsx-one-expression-per-line */
import type { OnboardingConfigItem } from '../../../authentication/types/types.auth';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { UserContext } from '@/shared/contexts/UserContext';
import { useApi } from '@shared/services/api';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import {
  getOnboardingIntent,
  OnboardingAction,
} from '@shared/utils/onboardingIntent';
import { trackPageView } from '../../../../third-party/ga';
import {
  AcquisitionSourceStep,
  DealInsightStep,
  HourlyRateStep,
} from './steps';

export interface OnboardingStepProps {
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
}

export interface OnboardingWizardProps {
  fullScreen?: boolean;
  onComplete?: (options?: { hasDealInsights?: boolean }) => void;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  fullScreen = false,
  onComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const { user, refetchUser } = useContext(UserContext);
  const { closeMessage } = useMessageService();
  const api = useApi();
  const [initialising, setInitialising] = useState(true);
  const initialisedRef = useRef(false);

  const [onboardingData, setOnboardingData] = useState<{
    welcomeCompleted?: boolean;
    dealInsights?: Array<{
      companyName: string;
      department: string;
      year: number;
      acv?: number;
      outcome?: 'won' | 'lost';
    }>;
    skippedSteps?: string[];
  }>({
    skippedSteps: [],
  });

  const steps = useMemo<('hourlyRate' | 'dealInsight' | 'source')[]>(
    () => ['hourlyRate', 'dealInsight', 'source'],
    [],
  );

  const totalSteps = steps.length;

  const stepConfig = {
    hourlyRate: {
      title: 'Set Your Consulting Rate',
      path: '/onboarding/hourly-rate',
    },
    dealInsight: {
      title: 'Share Your Deal Experience',
      path: '/onboarding/deal-insights',
    },
    source: {
      title: 'How Did You Hear About Us?',
      path: '/onboarding/acquisition-source',
    },
  };

  const currentStepKey = steps[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  useEffect(() => {
    const stepKey = steps[currentStep];
    trackPageView(
      stepConfig[stepKey].path,
      `Onboarding - ${stepConfig[stepKey].title}`,
    );
  }, [currentStep, steps]);

  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;
    refetchUser().finally(() => setInitialising(false));
  }, []);

  const submitOnboardingCompletion = useCallback(async () => {
    if (!user) {
      return;
    }
    const checklist = user.onboardingConfig || [];
    const updatedChecklist = checklist.length > 0
      ? checklist.map(
          (item: OnboardingConfigItem, idx: number) =>
            idx === 0 ? { ...item, completed: true } : item,
        )
      : [{ completed: true }];

    try {
      const acquisitionSourceData = sessionStorage.getItem('acquisitionSource');
      let source: string | undefined;
      let referer: string | undefined;

      if (acquisitionSourceData) {
        try {
          const sourceData = JSON.parse(acquisitionSourceData);
          source = sourceData.source;
          referer = sourceData.sourceDetails;
          sessionStorage.removeItem('acquisitionSource');
        }
        catch {
          // Silent fail
        }
      }

      const payload: any = {
        ...updatedChecklist,
      };

      if (source) {
        payload.source = source;
      }
      if (referer) {
        payload.referer = referer;
      }

      payload.onboardingStepData = {
        welcomeCompleted: onboardingData.welcomeCompleted,
        dealInsights: onboardingData.dealInsights,
        skippedSteps: onboardingData.skippedSteps,
      };

      await api.put(
        `${import.meta.env.VITE_API_BASE_URL}/user/${user.id}/onboarding`,
        payload,
      );
    }
    catch (error) {
    }
  }, [api, user, onboardingData]);

  const handleClose = async () => {
    await submitOnboardingCompletion();

    try {
      await api.post('subscription/assign-contributor', {});
    } catch {
      // Non-fatal
    }

    try {
      await refetchUser();
    } catch {
      // Non-fatal — user data will refresh on next navigation
    }
    closeMessage();

    const intent = getOnboardingIntent();

    if (
      intent?.action === OnboardingAction.CreateIntelligenceRequest
      && intent.companyId
      && intent.companyName
    ) {
      const actionData = {
        type: 'createIntelligenceRequest',
        companyId: intent.companyId,
        companyName: intent.companyName,
      };

      sessionStorage.setItem(
        'completedOnboardingAction',
        JSON.stringify(actionData),
      );
    }

    const hasDealInsights = (onboardingData.dealInsights?.length ?? 0) > 0;
    onComplete?.({ hasDealInsights });
  };

  const goNext = useCallback(() => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, totalSteps - 1));
  }, [totalSteps]);

  const goBack = useCallback(() => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, []);

  const handleDealInsightsChange = useCallback(
    (dealInsights: Array<{
      companyName: string;
      department: string;
      year: number;
      acv?: number;
      outcome?: 'won' | 'lost';
    }>) => {
      setOnboardingData(prev => ({
        ...prev,
        dealInsights,
      }));
    },
    [],
  );

  const handleDealInsightsComplete = useCallback(() => {
    if (!onboardingData.dealInsights || onboardingData.dealInsights.length === 0) {
      setOnboardingData(prev => ({
        ...prev,
        skippedSteps: [...(prev.skippedSteps || []), 'dealInsight'],
      }));
    }
    goNext();
  }, [goNext, onboardingData.dealInsights]);

  const slideVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 100 : -100,
    }),
    center: {
      opacity: 1,
      x: 0,
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir < 0 ? 100 : -100,
    }),
  };

  const renderStep = () => {
    switch (currentStepKey) {
      case 'hourlyRate':
        return (
          <HourlyRateStep
            onNext={goNext}
            onSkip={goNext}
          />
        );
      case 'dealInsight':
        return (
          <DealInsightStep
            onNext={handleDealInsightsComplete}
            onBack={currentStep > 0 ? goBack : undefined}
            onSkip={handleDealInsightsComplete}
            onChange={handleDealInsightsChange}
          />
        );
      case 'source':
        return (
          <AcquisitionSourceStep
            onNext={handleClose}
            onBack={goBack}
            onSkip={handleClose}
          />
        );
      default:
        return null;
    }
  };

  if (initialising) {
    return <LoadingSpinner variant="circular" size="large" message="Setting up your account..." fullScreen />;
  }

  const wizardClass = [
    'onboarding-wizard',
    fullScreen ? 'onboarding-wizard--fullscreen' : 'onboarding-wizard--inset',
  ].join(' ');

  return (
    <div className={wizardClass}>
      <header className="onboarding-wizard__header">
        <div className="onboarding-wizard__header-row">
          <p className="onboarding-wizard__eyebrow">
            Kandir Onboarding · Step {currentStep + 1} of {totalSteps}
          </p>
          <span className="onboarding-wizard__progress-chip">
            {Math.round(progress)}% Complete
          </span>
        </div>

        {currentStep === 0 && (
          <div className="onboarding-wizard__welcome">
            <h1 className="onboarding-wizard__welcome-title">
              Welcome to Kandir!
            </h1>
            <p className="onboarding-wizard__welcome-subtitle">
              Just a few quick steps to get you set up.
            </p>
          </div>
        )}

        <h2
          className={[
            'onboarding-wizard__step-title',
            currentStep === 0 ? 'onboarding-wizard__step-title--intro' : '',
          ].filter(Boolean).join(' ')}
        >
          {stepConfig[currentStepKey].title}
        </h2>

        <div className="onboarding-wizard__progress-rail">
          <motion.div
            className="onboarding-wizard__progress-fill"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          />
        </div>

        <div className="onboarding-wizard__progress-dots">
          {steps.map((stepKey, index) => {
            const stepProgress = ((index + 1) / totalSteps) * 100;
            let dotClass = 'onboarding-wizard__progress-dot';
            if (index < currentStep) dotClass += ' onboarding-wizard__progress-dot--complete';
            if (index === currentStep) dotClass += ' onboarding-wizard__progress-dot--current';
            return (
              <span
                key={stepKey}
                className={dotClass}
                style={{ left: `${stepProgress}%` }}
              />
            );
          })}
        </div>
      </header>

      <div className="onboarding-wizard__body">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            className="onboarding-wizard__body-motion"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              damping: 25,
              stiffness: 300,
              type: 'spring',
            }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OnboardingWizard;
